// A child's page under Workspaces (02-DESIGN/13-blueprints/01-the-blueprint.md
// § named blueprints, § the console: Workspaces; 02-DESIGN/03-console/01-the-console.md
// § the blueprint's page, § the surfaces: Workspaces; decisions 0052 and
// 0069), in tabs. Its title line carries *Open Northwind*, the way into the
// child at its own address in this tab: its sign-in page, or its Home where
// the person's sign-in admits them there; nothing is read across the
// relation. *Overview* says the **compliance** the last report carried,
// verified by this page against the carrier keys it holds, in one line —
// *version 7 applied of 7 · no drift · heard from 2 minutes ago* — with the
// findings opened in place, under the line. *Blueprint* names the blueprint
// it uses, a link to its page, and the version its document carries —
// *updating* while behind it — with *Change blueprint*, a form in the tab's
// place that says before it saves what the move adds and removes; the
// child's history, read-only, opened in place behind *History*; the
// blueprint's sections one line each, each a link to the blueprint's page on
// that tab, never redrawn here; the child's **secrets** as a table, one row
// per slot its blueprint names, whose row opens the field that seals a value
// — a form in the tab's place — toward the key the child's last verified
// report vouched, never shown again, *needed* while it has none and
// *re-enter* while its key is not vouched; and a document written before
// named blueprints shown as *its own blueprint*, its section lines
// read-only, with *Save as a named blueprint*.

import { blueprint } from "@impire-io/sdk";
import { useState } from "react";
import { Link, useLocation, useParams } from "react-router";

import { tenantPath } from "../../address/address.ts";
import { isRefusal } from "../../platform/bus.ts";
import {
  Button,
  Confirm,
  Dot,
  Empty,
  Field,
  NotAllowed,
  Notice,
  PageHead,
  Pending,
  Pill,
  plural,
  Select,
  TextInput,
  useLoad,
  when,
} from "../../ui/components.tsx";
import {
  Code,
  EXPECT_REVISION_HEADER,
  SERVICE,
  Subjects,
  slotOf,
  validName,
  type AssignRequest,
  type Document,
  type Entry,
  type HistoryReply,
  type LibraryDocument,
  type LibraryReply,
  type Secret,
  type StatusReply,
} from "../../wire/blueprint.ts";
import { CouldNotShow } from "../../ui/boundary.tsx";
import { FormPage, useFormState, type FormState } from "../../ui/form.tsx";
import { NameLink } from "../../ui/ident.tsx";
import { Table, type Column } from "../../ui/table.tsx";
import { Tabs } from "../../ui/tabs.tsx";
import { SERVICE as IDENTITY } from "../../wire/identity.ts";
import { Subjects as AuthSubjects, type ChildrenListReply } from "../../wire/auth.ts";
import { useSurface } from "../surface.tsx";
import { useMoves } from "../workspace/moves.tsx";
import {
  attest,
  consequences,
  driftName,
  driftWords,
  reenter,
  secretKey,
  sectionLines,
  type Attested,
} from "./blueprint.ts";
import { toLibrary } from "./forms.ts";
import { complianceLine } from "./compliance.ts";
import { BLUEPRINTS_TAB, blueprintFailure, blueprintPath, nameFrom, sealedSecrets } from "./library.ts";
import { childName } from "./relation.ts";
import { CollectAction, zeroTrustFormDrawn, type ZeroTrustState } from "../settings/shares.tsx";
import { Slot, type EscrowStatus } from "../../wire/escrow.ts";
import { ChildZeroTrust, useChildZeroTrust } from "./ChildRecovery.tsx";
import { childFormTab, childInitialTab, ZERO_TRUST_TAB, type ChildTab } from "./recovery.ts";

/** readyStatus is the shares' status once read. */
function readyStatus(z: ZeroTrustState): EscrowStatus | undefined {
  return z.st.state === "ready" ? z.st.value : undefined;
}

/** CHANGE is the Blueprint tab's *Change blueprint* form; SEAL names a secret's form, `seal:<slot>`. */
const CHANGE = "change";
const SEAL = "seal:";

export function ChildPage() {
  const { child = "" } = useParams();
  return <Child key={child} child={child} />;
}

/** absent reads a not-found refusal as absence. */
function absent<T>(read: Promise<T>): Promise<T | undefined> {
  return read.catch((err: unknown) => {
    if (isRefusal(err, Code.notFound)) {
      return undefined;
    }
    throw err;
  });
}

/** parsed reads a document's text — a child's or a named blueprint's — or nothing where it is none. */
function parsed(text: string | undefined): Document | undefined {
  if (text === undefined) {
    return undefined;
  }
  try {
    return JSON.parse(text) as Document;
  } catch {
    return undefined;
  }
}

/** withoutSecrets is a document as what it requires, the secrets — each workspace's own — aside. */
function withoutSecrets(d: Document | LibraryDocument | undefined): Document | undefined {
  if (d === undefined) {
    return undefined;
  }
  const rest: Document = { ...(d as Document) };
  delete rest.secrets;
  return rest;
}

function Child({ child }: { child: string }) {
  const s = useSurface();
  const moves = useMoves();
  const [tick, setTick] = useState(0);
  // The page's tabs (01-the-console.md § conventions): *Overview* first. A
  // form open on a tab keeps that tab — *Blueprint*'s, or *Zero-trust*'s
  // Collect and Present: a refresh with it open shows the form again. A
  // link may carry the tab in its navigation state, as the Workspaces row's
  // *Recovery shares* does.
  const form = useFormState();
  const location = useLocation();
  const [chosen, setChosen] = useState<ChildTab>(() => childInitialTab(form.open, location.state));
  const tab = childFormTab(form.open) ?? chosen;
  // A tab picked while a form stands in a tab's place closes the form.
  const setTab = (t: ChildTab) => {
    if (form.open !== undefined) {
      form.close();
    }
    setChosen(t);
  };
  // What the last change said, kept across the read that follows it.
  const [said, setSaid] = useState<string | undefined>(undefined);
  const reload = () => {
    setTick((t) => t + 1);
  };
  const changed = (what?: string) => {
    setSaid(what);
    reload();
  };
  const [doc] = useLoad(
    `blueprint:${child}`,
    () => absent(s.bus.call<Entry>(SERVICE, Subjects.get(child), {})),
    tick,
  );
  const [attested] = useLoad(
    `status:${child}`,
    async (): Promise<Attested> => {
      const status = await absent(s.bus.call<StatusReply>(SERVICE, Subjects.status(child), undefined));
      return attest(status, await s.bus.carrierKeys(), child);
    },
    tick,
  );
  const [history] = useLoad(
    `history:${child}`,
    () => absent(s.bus.call<HistoryReply>(SERVICE, Subjects.history(child), undefined)),
    tick,
  );
  const [library] = useLoad(
    "library",
    () => s.bus.call<LibraryReply>(SERVICE, Subjects.libraryList(), undefined),
    tick,
  );
  // Its name: the row of the children list *Workspaces* reads.
  const [children] = useLoad("children", () =>
    s.bus.call<ChildrenListReply>(IDENTITY, AuthSubjects.childrenList(), undefined),
  );
  const row = children.state === "ready" ? children.value.children.find((c) => c.slug === child) : undefined;
  const name = row === undefined ? child : childName(row);
  // *Zero-trust*: the parent's part of the child's recovery shares (spec 061).
  const z = useChildZeroTrust(child);
  const between = { child: name, parent: s.name };
  // *Open Northwind*: the child's own address, in this tab.
  const open =
    moves === undefined ? undefined : (
      <Button
        onClick={() => {
          moves.enter(child);
        }}
      >
        Open {name}
      </Button>
    );
  // While the parent's share waits, *Collect* is the Zero-trust tab's primary action.
  const collect =
    tab === ZERO_TRUST_TAB && !zeroTrustFormDrawn(form.open, z.collected !== undefined, readyStatus(z)) ? (
      <CollectAction z={z} mine={Slot.parent} />
    ) : undefined;
  const action =
    collect === undefined ? (
      open
    ) : (
      <>
        {open}
        {collect}
      </>
    );
  const entry = doc.state === "ready" && doc.value?.op === blueprint.EntryOp.put ? doc.value : undefined;
  const uses = entry?.blueprint;
  const [named] = useLoad(
    `library:${uses ?? ""}`,
    () =>
      uses === undefined || uses === ""
        ? Promise.resolve(undefined)
        : absent(s.bus.call<Entry>(SERVICE, Subjects.libraryGet(uses), {})),
    tick,
  );
  const crumb = (
    <>
      <Link to={tenantPath(s.tenant, "workspaces")}>Workspaces</Link>
      <span>/</span>
      <span>{name}</span>
    </>
  );
  if (doc.state === "pending") {
    return (
      <>
        <div className="crumb">{crumb}</div>
        <Pending what="Reading its blueprint" />
      </>
    );
  }
  // A blueprint read that did not answer fails the Blueprint tab alone: the
  // tabs stay, and Overview draws what it holds (§ conventions: a reply it
  // cannot draw).
  const failed = doc.state === "refused" ? blueprintFailure(doc.err) : undefined;
  const a: Attested = attested.state === "ready" ? attested.value : { state: "none" };
  const blueprints = library.state === "ready" ? (library.value.blueprints ?? []) : [];
  const namedEntry = named.state === "ready" ? named.value : undefined;
  const behind = namedEntry !== undefined && entry?.from !== undefined && namedEntry.revision > entry.from;
  return (
    <>
      <PageHead
        crumb={crumb}
        title={name}
        action={action}
        line={
          failed !== undefined ? undefined : entry === undefined ? (
            "No blueprint yet: the workspace is its own. Applying one makes it managed, and places its steward."
          ) : uses === undefined || uses === "" ? (
            `Its own blueprint, version ${String(entry.revision)}, written ${entry.author === blueprint.AUTHOR_OPERATOR ? "by the install's operator" : "before blueprints were kept by name"}.`
          ) : (
            <>
              Uses the blueprint <NameLink to={blueprintPath(s.tenant, uses)}>{uses}</NameLink>, at its
              version {String(entry.from ?? 0)}
              {behind ? " — updating to the latest" : ""}.
            </>
          )
        }
      />
      {said !== undefined && <Notice kind="ok">{said}</Notice>}
      <Tabs
        label={name}
        tabs={[
          { id: "overview", label: "Overview" },
          { id: "blueprint", label: "Blueprint" },
          { id: ZERO_TRUST_TAB, label: "Zero-trust" },
        ]}
        value={tab}
        onChange={setTab}
      >
        {tab === ZERO_TRUST_TAB ? (
          <ChildZeroTrust z={z} slug={child} between={between} />
        ) : tab === "overview" ? (
          <div className="stack">
            <dl className="kv">
              <dt>First admin</dt>
              <dd>
                {row === undefined ? (
                  <span className="faint">{children.state === "pending" ? "reading…" : "not known"}</span>
                ) : row.pending !== undefined && row.pending !== "" ? (
                  <>
                    <span className="mono">{row.pending}</span> <Pill>waiting</Pill>
                  </>
                ) : (
                  "signed in"
                )}
              </dd>
            </dl>
            {attested.state === "refused" ? (
              <section className="section">
                <div className="section-head">
                  <h2>Compliance</h2>
                </div>
                <p className="muted">
                  Its compliance could not be read: {blueprintFailure(attested.err).message}
                </p>
              </section>
            ) : (
              <Compliance written={entry?.revision} a={a} />
            )}
          </div>
        ) : failed !== undefined ? (
          <CouldNotShow part="tab" err={failed} onRetry={reload} />
        ) : form.open === CHANGE ? (
          <ChangeBlueprint
            child={child}
            name={name}
            entry={entry}
            library={blueprints.map((b) => b.name)}
            onClose={form.close}
            onChanged={(said) => {
              form.close();
              changed(said);
            }}
          />
        ) : form.open?.startsWith(SEAL) === true &&
          entry !== undefined &&
          uses !== undefined &&
          uses !== "" ? (
          <SealSecret
            child={child}
            name={name}
            entry={entry}
            slot={form.open.slice(SEAL.length)}
            slots={parsed(namedEntry?.document)?.secrets ?? []}
            attested={a}
            onClose={form.close}
            onChanged={(said) => {
              form.close();
              changed(said);
            }}
          />
        ) : (
          <div className="stack">
            <Uses
              entry={entry}
              behind={behind}
              library={blueprints.map((b) => b.name)}
              history={history.state === "ready" ? (history.value?.entries ?? []) : []}
              form={form}
            />
            {entry !== undefined && uses !== undefined && uses !== "" && (
              <Sections uses={uses} doc={parsed(namedEntry?.document)} />
            )}
            {entry !== undefined && uses !== undefined && uses !== "" && (
              <Secrets
                entry={entry}
                slots={parsed(namedEntry?.document)?.secrets ?? []}
                attested={a}
                onOpen={(slot) => {
                  form.show(`${SEAL}${slot}`);
                }}
              />
            )}
            {entry !== undefined && (uses === undefined || uses === "") && (
              <OwnBlueprint
                child={child}
                entry={entry}
                taken={blueprints.map((b) => b.name)}
                onChanged={changed}
              />
            )}
          </div>
        )}
      </Tabs>
    </>
  );
}

/**
 * Uses is the blueprint the child uses, its name a link to its page, with
 * *Change blueprint* — a form in the tab's place — and the child's history,
 * opened in place behind *History*.
 */
function Uses({
  entry,
  behind,
  library,
  history,
  form,
}: {
  entry: Entry | undefined;
  behind: boolean;
  library: string[];
  /** The child's versions, behind *History*. */
  history: Entry[];
  form: FormState<string>;
}) {
  const s = useSurface();
  const [showHistory, setShowHistory] = useState(false);
  const current = entry?.blueprint ?? "";
  const others = library.filter((n) => n !== current);
  // *Change blueprint* is offered where there is another to choose, or a blueprint to remove.
  const changeable = others.length > 0 || entry !== undefined;
  return (
    <div className="card section stack">
      <div className="section-head">
        <h2>Blueprint</h2>
        <span className="row" style={{ gap: 8 }}>
          {history.length > 0 && (
            <Button
              kind="quiet"
              size="sm"
              onClick={() => {
                setShowHistory((o) => !o);
              }}
            >
              {showHistory ? "Hide history" : "History"}
            </Button>
          )}
          {changeable && (
            <Button
              size="sm"
              onClick={() => {
                form.show(CHANGE);
              }}
            >
              {entry === undefined ? "Apply a blueprint" : "Change blueprint"}
            </Button>
          )}
        </span>
      </div>
      <p className="small muted">
        {entry === undefined ? (
          "None: the workspace decides everything itself."
        ) : current === "" ? (
          "Its own blueprint, written on this workspace alone. Applying one of yours replaces it."
        ) : (
          <>
            It uses <NameLink to={blueprintPath(s.tenant, current)}>{current}</NameLink>, version{" "}
            {String(entry.from ?? 0)}
            {behind ? (
              <>
                {" "}
                · <Pill tone="info">updating</Pill>
              </>
            ) : null}
            . A change to {current} reaches it, and every other workspace using it.
          </>
        )}
      </p>
      {!changeable && (
        <p className="small faint">
          You keep no blueprint yet:{" "}
          <Link className="text-link" to={tenantPath(s.tenant, "workspaces")} state={{ tab: BLUEPRINTS_TAB }}>
            make one
          </Link>
          .
        </p>
      )}
      {showHistory && <History entries={history} />}
    </div>
  );
}

/**
 * ChangeBlueprint is *Change blueprint* in the Blueprint tab's place: another
 * of the library, saying before it saves what the move adds and removes
 * there, or none.
 */
function ChangeBlueprint({
  child,
  name,
  entry,
  library,
  onClose,
  onChanged,
}: {
  child: string;
  name: string;
  entry: Entry | undefined;
  library: string[];
  onClose: () => void;
  onChanged: (said?: string) => void;
}) {
  const s = useSurface();
  const current = entry?.blueprint ?? "";
  const others = library.filter((n) => n !== current);
  const [to, setTo] = useState(others[0] ?? "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(undefined);
  const [target] = useLoad(`library:${to}`, () =>
    to === "" ? Promise.resolve(undefined) : absent(s.bus.call<Entry>(SERVICE, Subjects.libraryGet(to), {})),
  );
  const before = withoutSecrets(parsed(entry?.document));
  const after = target.state === "ready" ? withoutSecrets(parsed(target.value?.document)) : undefined;
  const lines = to === "" ? [] : consequences(before, after ?? {});
  const apply = () => {
    if (busy || to === "") {
      return;
    }
    setBusy(true);
    setErr(undefined);
    // The secrets are left out: the service keeps those whose slot the new blueprint shares.
    const body: AssignRequest = { blueprint: to };
    const extra = entry !== undefined ? { [EXPECT_REVISION_HEADER]: String(entry.revision) } : undefined;
    s.bus.callText<Entry>(SERVICE, Subjects.assign(child), JSON.stringify(body), extra).then(
      () => {
        setBusy(false);
        onChanged(`${name} uses the blueprint ${to} now; its steward takes it at its next pass.`);
      },
      (e: unknown) => {
        setBusy(false);
        setErr(e);
      },
    );
  };
  const remove = () => {
    setErr(undefined);
    s.bus.call<Entry>(SERVICE, Subjects.delete(child), undefined).then(
      () => {
        onChanged(
          "The workspace has no blueprint now. Its steward lowers everything the blueprint put there, then leaves; what the workspace's admins wrote stays theirs.",
        );
      },
      (e: unknown) => {
        setErr(e);
      },
    );
  };
  return (
    <FormPage
      heading="h2"
      title={entry === undefined ? "Apply a blueprint" : "Change blueprint"}
      line={
        entry === undefined
          ? `${name} becomes managed: its steward is placed and makes it what the blueprint says.`
          : current === ""
            ? "Its own blueprint is replaced by the one you choose."
            : `It uses ${current} now. The secrets whose slot the new blueprint shares are kept.`
      }
      back="Blueprint"
      onClose={onClose}
      foot={
        <>
          <Button kind="quiet" onClick={onClose}>
            Cancel
          </Button>
          {entry !== undefined && (
            <Confirm
              label="No blueprint"
              consequence="Its steward lowers everything the blueprint put there, then leaves the workspace."
              onConfirm={remove}
            />
          )}
          {others.length > 0 && (
            <Button kind="primary" disabled={busy || to === ""} onClick={apply}>
              {busy ? "Applying…" : entry === undefined ? "Apply, and manage this workspace" : `Use ${to}`}
            </Button>
          )}
        </>
      }
    >
      {err !== undefined &&
        (isRefusal(err, Code.conflict) ? (
          <Notice kind="bad" title="Its blueprint changed after you opened this page">
            Nothing was changed.{" "}
            <button
              type="button"
              className="text-link"
              onClick={() => {
                onChanged();
              }}
            >
              Reload
            </button>
          </Notice>
        ) : (
          <NotAllowed err={err} what="Change blueprint" />
        ))}
      {others.length === 0 ? (
        <p className="small faint">You keep no other blueprint.</p>
      ) : (
        <>
          <Field label="Blueprint">
            <Select value={to} onChange={setTo} options={others.map((n) => ({ value: n, label: n }))} />
          </Field>
          <div>
            <h3>What this does in {name}</h3>
            {lines.length === 0 ? (
              <p className="small muted">Nothing it does not already do.</p>
            ) : (
              <ul className="small">
                {lines.map((l) => (
                  <li key={l}>{l}</li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </FormPage>
  );
}

/**
 * Sections is the blueprint the child uses, one line per section, each a
 * link to the blueprint's page on that tab (01-the-console.md § the
 * blueprint's page): never redrawn here, and never a second row of tabs.
 * Without `uses` the lines are read-only — a child's own blueprint.
 */
function Sections({ uses, doc }: { uses?: string; doc: Document | LibraryDocument | undefined }) {
  const s = useSurface();
  return (
    <div className="card section stack">
      <div className="section-head">
        <h2>What it requires</h2>
        {uses !== undefined && <span className="small faint">each opens {uses} on that tab</span>}
      </div>
      <div className="list">
        {sectionLines(doc).map((l) => (
          <div className="li" key={l.key}>
            {uses === undefined ? (
              <span className="grow">
                {l.title} <span className="muted">— {l.words}</span>
              </span>
            ) : (
              <Link
                className="grow text-link"
                to={tenantPath(s.tenant, "workspaces", "blueprints", uses)}
                state={{ tab: l.tab }}
              >
                {l.title} <span className="muted">— {l.words}</span>
              </Link>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/** SlotRow is one secret slot of the child's blueprint, and the child's value for it where it has one. */
interface SlotRow {
  slot: string;
  forHandle: string;
  name: string;
  held: Secret | undefined;
  reenter: boolean;
}

/** slotRows are the child's blueprint's secret slots, each with the child's value where it has one. */
function slotRows(
  entry: Entry,
  slots: readonly { for: string; name: string }[],
  attested: Attested,
): SlotRow[] {
  const held = sealedSecrets(parsed(entry.document));
  return slots.map((slot) => {
    const h = held.find((x) => slotOf(x) === slotOf(slot));
    return {
      slot: slotOf(slot),
      forHandle: slot.for,
      name: slot.name,
      held: h,
      reenter: h !== undefined && reenter(attested, slot.for, slot.name),
    };
  });
}

/**
 * Secrets is the child's own values for its blueprint's secret slots, as a
 * table: a row opens the field that seals a value, in the tab's place.
 */
function Secrets({
  entry,
  slots,
  attested,
  onOpen,
}: {
  entry: Entry;
  slots: readonly { for: string; name: string }[];
  attested: Attested;
  onOpen: (slot: string) => void;
}) {
  const key = secretKey(attested);
  const rows = slotRows(entry, slots, attested);
  const columns: Column<SlotRow>[] = [
    {
      key: "slot",
      header: "Secret",
      value: (r) => r.slot,
      cell: (r) => (
        <>
          <span className="cell-main mono">{r.slot}</span>
          <span className="cell-sub">
            {r.held === undefined
              ? "no value yet"
              : `version ${String(r.held.version)} · sealed, never shown again`}
          </span>
        </>
      ),
    },
    {
      key: "state",
      header: "State",
      width: "120px",
      value: (r) => (r.held === undefined ? "needed" : r.reenter ? "re-enter" : "held"),
      facet: "State",
      cell: (r) =>
        r.held === undefined ? (
          <Pill tone="bad">needed</Pill>
        ) : r.reenter ? (
          <Pill tone="bad">re-enter</Pill>
        ) : (
          <Pill tone="ok">held</Pill>
        ),
    },
  ];
  return (
    <div className="card section stack">
      <div className="section-head">
        <h2>Secrets</h2>
        <span className="small faint">this workspace&apos;s own values for what {entry.blueprint} needs</span>
      </div>
      {slots.length === 0 ? (
        <p className="faint">Its blueprint needs no secret.</p>
      ) : (
        <>
          {key === undefined && (
            <p className="small muted">
              A secret is sealed toward the key the workspace&apos;s last report vouched: available once the
              workspace has reported.
            </p>
          )}
          <Table
            label="Secrets"
            rows={rows}
            columns={columns}
            rowKey={(r) => r.slot}
            sort={{ key: "slot", dir: "asc" }}
            onOpen={(r) => {
              onOpen(r.slot);
            }}
          />
        </>
      )}
    </div>
  );
}

/**
 * SealSecret is a secret's field in the Blueprint tab's place: the value is
 * sealed here toward the key the child's last verified report vouched, which
 * only the child's vault can open, and is never shown again.
 */
function SealSecret({
  child,
  name,
  entry,
  slot,
  slots,
  attested,
  onClose,
  onChanged,
}: {
  child: string;
  name: string;
  entry: Entry;
  slot: string;
  slots: readonly { for: string; name: string }[];
  attested: Attested;
  onClose: () => void;
  onChanged: (said?: string) => void;
}) {
  const s = useSurface();
  const key = secretKey(attested);
  const held = sealedSecrets(parsed(entry.document));
  const picked = slotRows(entry, slots, attested).find((r) => r.slot === slot);
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(undefined);
  const ready = !busy && key !== undefined && value !== "" && picked !== undefined;
  const seal = () => {
    if (!ready || entry.blueprint === undefined) {
      return;
    }
    setBusy(true);
    setErr(undefined);
    const next: Secret[] = held.filter((h) => slotOf(h) !== picked.slot);
    next.push({
      for: picked.forHandle,
      name: picked.name,
      ...blueprint.sealSecret(value, key),
      version: (picked.held?.version ?? 0) + 1,
    });
    const body: AssignRequest = { blueprint: entry.blueprint, secrets: next };
    s.bus
      .callText<Entry>(SERVICE, Subjects.assign(child), JSON.stringify(body), {
        [EXPECT_REVISION_HEADER]: String(entry.revision),
      })
      .then(
        () => {
          setBusy(false);
          onChanged(`Sealed ${picked.slot} for ${name}; its steward stores it at its next pass.`);
        },
        (e: unknown) => {
          setBusy(false);
          setErr(e);
        },
      );
  };
  return (
    <FormPage
      heading="h2"
      title={<span className="mono">{slot}</span>}
      line={
        picked === undefined
          ? `${entry.blueprint ?? "Its blueprint"} names no such secret.`
          : picked.held === undefined
            ? "No value yet."
            : `Version ${String(picked.held.version)} is held, sealed and never shown again; a new value replaces it.`
      }
      back="Blueprint"
      onClose={onClose}
      foot={
        <>
          <Button kind="quiet" onClick={onClose}>
            Cancel
          </Button>
          {key !== undefined && picked !== undefined && (
            <Button kind="primary" disabled={!ready} onClick={seal}>
              {busy ? "Sealing…" : "Seal"}
            </Button>
          )}
        </>
      }
    >
      {err !== undefined && <NotAllowed err={err} what="Seal" />}
      {picked === undefined ? null : key === undefined ? (
        <p className="small muted">Available once the workspace has reported.</p>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            seal();
          }}
        >
          <Field
            label={picked.held === undefined ? "Value" : "New value"}
            hint={`Sealed here toward ${name}'s vault, which alone can open it; never shown again.`}
          >
            <TextInput value={value} onChange={setValue} type="password" autoFocus />
          </Field>
        </form>
      )}
    </FormPage>
  );
}

/**
 * OwnBlueprint is a document written before named blueprints, or by the
 * install's operator: read-only here, with *Save as a named blueprint*,
 * which writes it to the library with its secrets as slots and applies it,
 * the workspace's sealed values kept as its own.
 */
function OwnBlueprint({
  child,
  entry,
  taken,
  onChanged,
}: {
  child: string;
  entry: Entry;
  taken: string[];
  onChanged: (said?: string) => void;
}) {
  const s = useSurface();
  const doc = parsed(entry.document);
  const [name, setName] = useState(nameFrom(child));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(undefined);
  const clash = taken.includes(name);
  const ok = validName(name) && !clash && doc !== undefined;
  const save = () => {
    if (busy || !ok) {
      return;
    }
    setBusy(true);
    setErr(undefined);
    s.bus
      .callText<Entry>(SERVICE, Subjects.librarySet(name), JSON.stringify(toLibrary(doc), null, 2), {
        [EXPECT_REVISION_HEADER]: "0",
      })
      .then(() => {
        const body: AssignRequest = { blueprint: name, secrets: sealedSecrets(doc) };
        return s.bus.callText<Entry>(SERVICE, Subjects.assign(child), JSON.stringify(body), {
          [EXPECT_REVISION_HEADER]: String(entry.revision),
        });
      })
      .then(
        () => {
          setBusy(false);
          onChanged(`Saved as the blueprint ${name}; ${child} uses it now.`);
        },
        (e: unknown) => {
          setBusy(false);
          setErr(e);
        },
      );
  };
  return (
    <div className="card section stack">
      <div className="section-head">
        <h2>Its own blueprint</h2>
        <span className="small faint">read-only</span>
      </div>
      <div className="list">
        {sectionLines(doc).map((l) => (
          <div className="li" key={l.key}>
            <span className="grow">
              {l.title} <span className="muted">— {l.words}</span>
            </span>
          </div>
        ))}
      </div>
      {err !== undefined && <NotAllowed err={err} what="Save as a named blueprint" />}
      <Field
        label="Save as a named blueprint"
        hint={
          clash
            ? "You keep a blueprint by that name already."
            : "It joins your blueprints under this name, and this workspace uses it; its secrets stay this workspace's own."
        }
      >
        <TextInput value={name} onChange={setName} mono />
      </Field>
      <div className="row" style={{ justifyContent: "flex-end" }}>
        <Button kind="primary" disabled={busy || !ok} onClick={save}>
          {busy ? "Saving…" : "Save as a named blueprint"}
        </Button>
      </div>
    </div>
  );
}

/**
 * Compliance is the child's compliance in one line — the version applied, the
 * drift, when it was last heard from — with the findings opened in place,
 * under the line: what is not yet applied, each drift, the required workloads.
 */
function Compliance({ written, a }: { written: number | undefined; a: Attested }) {
  const [open, setOpen] = useState(false);
  const line = complianceLine(written, a);
  return (
    <section className="section">
      <div className="section-head">
        <h2>Compliance</h2>
      </div>
      <div className="row">
        <Dot tone={line.tone} />
        <span className="grow">{line.text}</span>
        {line.findings && (
          <Button
            size="sm"
            onClick={() => {
              setOpen((o) => !o);
            }}
          >
            {open ? "Hide findings" : "Findings"}
          </Button>
        )}
      </div>
      {open && (
        <div className="stack" style={{ marginTop: 12 }}>
          <Findings written={written} a={a} />
        </div>
      )}
    </section>
  );
}

/** Findings is what the child's last report attested, in full, under the compliance line. */
function Findings({ written, a }: { written: number | undefined; a: Attested }) {
  if (a.state === "none" || a.state === "not-yet") {
    return <p className="muted">Not yet attested: the workspace&apos;s steward has not reported.</p>;
  }
  if (a.state === "refused") {
    return (
      <Notice kind="bad" title="Its last report does not verify">
        The report that arrived {when(a.received)} was refused ({a.code}); nothing it says is shown.
      </Notice>
    );
  }
  const r = a.report;
  const failed = (r.items ?? []).filter((i) => i.state !== blueprint.ItemState.applied);
  return (
    <>
      {a.silent && (
        <Notice kind="bad" title={`Not heard from since ${when(a.received)}`}>
          Its steward has been silent longer than a report lives. What follows is the last state it attested.
        </Notice>
      )}
      <dl className="kv">
        <dt>Version applied</dt>
        <dd>
          {r.revision_applied} of {written ?? r.revision_seen}
          {written !== undefined && r.revision_applied === written ? (
            <Pill tone="ok">true to the blueprint</Pill>
          ) : (
            <Pill tone="info">converging</Pill>
          )}
        </dd>
        <dt>Last heard</dt>
        <dd>{when(a.received)}</dd>
        <dt>Not yet applied</dt>
        <dd>
          {failed.length === 0 ? (
            <span className="faint">nothing</span>
          ) : (
            failed.map((i) => (
              <div key={`${i.section}.${i.handle}`}>
                <span className="mono">{i.handle}</span> {i.state}
                {i.message !== undefined && <span className="muted"> · {i.message}</span>}
              </div>
            ))
          )}
        </dd>
        <dt>Drift</dt>
        <dd>
          {(r.drift ?? []).length === 0 ? (
            <span className="faint">none</span>
          ) : (
            (r.drift ?? []).map((d) => (
              <div key={`${d.kind}.${d.names}`}>
                <span className="mono">{driftName(d)}</span> {driftWords(d.kind)}
                {d.detail !== undefined && <span className="muted"> · {d.detail}</span>}
                <span className="small faint"> since {when(d.since)}</span>
              </div>
            ))
          )}
        </dd>
        <dt>Required workloads</dt>
        <dd>
          {(r.workloads ?? []).length === 0 ? (
            <span className="faint">none</span>
          ) : (
            (r.workloads ?? []).map((w) => (
              <div key={w.handle}>
                <span className="mono">{w.handle}</span> {w.status ?? "unknown"} · {w.running} of{" "}
                {plural(w.wanted, "copy", "copies")} running
              </div>
            ))
          )}
        </dd>
      </dl>
    </>
  );
}

/** History is the child's own versions, read-only, each naming the blueprint it came from. */
function History({ entries }: { entries: Entry[] }) {
  return (
    <div className="stack" style={{ marginTop: 12 }}>
      <h3>History</h3>
      {entries.length === 0 ? (
        <Empty>No earlier version.</Empty>
      ) : (
        entries.map((e) => (
          <div className="row" key={e.revision}>
            <span>version {e.revision}</span>
            <span className="small muted">
              {e.op === blueprint.EntryOp.delete
                ? "removed"
                : e.blueprint !== undefined && e.blueprint !== ""
                  ? `from ${e.blueprint}, its version ${String(e.from ?? 0)}`
                  : "its own"}
              {e.author !== undefined ? ` · ${e.author}` : ""}{" "}
              {e.created !== undefined ? when(e.created) : ""}
            </span>
          </div>
        ))
      )}
    </div>
  );
}
